// Resolves a `gasFeePayment` into a "{amount} {symbol}" string, reusing the same
// asset-info resolution the legacy Footer uses. Shared by the public + private
// activity rows so both format fees identically.
import { formatUnits, ZeroAddress } from 'ethers'
import { useEffect, useState } from 'react'

import gasTankFeeTokens from '@ambire-common/consts/gasTankFeeTokens'
import { Network } from '@ambire-common/interfaces/network'
import { GasFeePayment } from '@ambire-common/libs/accountOp/accountOp'
import { resolveAssetInfo } from '@ambire-common/services/assetInfo'
import formatDecimals from '@ambire-common/utils/formatDecimals/formatDecimals'
import { useTranslation } from '@common/config/localization'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'

export const useFeeFormatted = (
  gasFeePayment: GasFeePayment | null | undefined,
  network: Network | undefined
) => {
  const { networks } = useNetworksControllerState()
  const { t } = useTranslation()
  const [feeText, setFeeText] = useState<string>()

  useEffect(() => {
    const feeTokenAddress = gasFeePayment?.inToken
    const chainId = network?.chainId
    const nChainId =
      gasFeePayment?.feeTokenChainId ||
      (feeTokenAddress === ZeroAddress && chainId) ||
      gasTankFeeTokens.find((f: any) => f.address === feeTokenAddress)?.chainId ||
      chainId
    const tokenNetwork = networks.find((n: Network) => n.chainId === nChainId)
    const feeTokenAmount = gasFeePayment?.amount

    if (!feeTokenAddress || !tokenNetwork || !feeTokenAmount) {
      setFeeText(undefined)
      return
    }

    setFeeText(undefined)
    resolveAssetInfo(feeTokenAddress, tokenNetwork, ({ tokenInfo }) => {
      if (!tokenInfo || !gasFeePayment?.amount) return
      const fee = parseFloat(formatUnits(feeTokenAmount, tokenInfo.decimals))
      setFeeText(`${formatDecimals(fee)} ${tokenInfo.symbol}`)
    }).catch(() => setFeeText('—'))
  }, [
    networks,
    network?.chainId,
    gasFeePayment?.feeTokenChainId,
    gasFeePayment?.amount,
    gasFeePayment?.inToken
  ])

  const isLoading = !!gasFeePayment?.isSponsored
    ? false
    : !!gasFeePayment?.inToken && !!gasFeePayment?.amount && !feeText

  return { feeText: gasFeePayment?.isSponsored ? t('Sponsored') : feeText, isLoading }
}

export default useFeeFormatted
